import styled from '@emotion/styled';
import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Button,
  RadioGroup,
  Radio as _Radio,
  FormControlLabel,
  FormLabel as _FormLabel,
  FormControl,
  CardActions as _CardActions,
  CardContent as _CardContent,
  CardMedia as _CardMedia,
  Drawer,
  Grid,
  Typography,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import _HelpIcon from '@material-ui/icons/Help';

import globalState from '../../../../operations/commands';
import { Organization, GoogleSearchConsoleSite, LinkSearchConsoleSitesDocument } from '../../../../generated/graphql';
import { COLOR_TEXT_MAIN } from '../../../../theme/colors';
import LoadingButton from '../../primitive/LoadingButton';
import mutationVariables from '../../../../utility/mutationVariables';

type Props = {
  sites: Array<GoogleSearchConsoleSite>;
  organizationId: Organization['id'];
  accountId: string | null;
  setAccountId: (accountId: string | null) => void;
  open: boolean;
  toggleDrawer: (open: boolean) => void;
  refetchSites: (variables: any) => void;
};

const AvailableSitesDrawer: FC<Props> = ({
  sites,
  organizationId,
  accountId,
  setAccountId,
  open,
  toggleDrawer,
  refetchSites,
}) => {
  const [selectedSiteUrl, setSelectedSiteUrl] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);

  // if this mutation can return data with linked site, there is no need to refetch getSites
  const [linkSites, { loading: linkSitesLoading }] = useMutation(LinkSearchConsoleSitesDocument, {
    context: { clientName: 'search_console' },
    ...mutationVariables({
      organization_id: organizationId,
      account_id: accountId,
      site_urls: [selectedSiteUrl],
    }),
    onCompleted: () => {
      globalState.setErrors({
        message: '連携に成功しました。レポート分析が完了するまで、しばらくお待ち下さい',
        name: 'UnimportantError',
        context: { clientName: 'search_console' },
      }),
        setAccountId(null); // 組織切り替え時にdrawerがいきなり開かないように、連携が完了したらnullに変更しておく
      closeDrawer();
      refetchSites({
        organization_id: organizationId,
        account_id: accountId,
      });
    },
    onError: (error) =>
      globalState.setErrors({
        message: error.message,
        name: 'NonFatalError',
        context: { clientName: 'search_console' },
      }),
  });

  const closeDrawer = () => {
    setSelectedSiteUrl('');
    toggleDrawer(false);
  };

  const handleSiteUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canSubmit) {
      setCanSubmit(true);
    }

    setSelectedSiteUrl(event.target.value);
    const site = sites.find((site) => site.site_url === event.target.value);
    setAccountId(site?.account_id || null);
  };

  const handleSubmit = () => {
    if (organizationId && accountId) linkSites();
  };

  return (
    <Drawer anchor="right" open={open}>
      <DrawerContainer>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>GoogleSearchConsole 連携</DrawerTitle>
          </DrawerHeader>
          <Content>
            <DrawerDescription>連携するサイトを選択して下さい</DrawerDescription>
            <Grid container spacing={3}>
              <GridItem item xs={12}>
                <FormControl fullWidth>
                  <RadioGroup aria-label="サイト一覧" name="row-radio-buttons-group" onChange={handleSiteUrlChange}>
                    {sites?.map((site, index) => (
                      <FormControlLabel
                        key={index}
                        value={site.site_url}
                        control={<Radio disableRipple />}
                        label={site.site_url}
                        disabled={!site.has_permission}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </GridItem>
            </Grid>
            <DrawerCaption>
              グレーアウトされて選択できないサイトについて
              <Tooltip
                title={
                  <div>
                    <TooltipText>以下のサイトは選択できません</TooltipText>
                    <TooltipText>・Google Search Console上でサイトの閲覧権限が付与されていない</TooltipText>
                    <TooltipText>・サイトのGoogle Search Consoleの設定が完了していない</TooltipText>
                  </div>
                }
                placement="bottom"
              >
                <IconButton size="small">
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            </DrawerCaption>
          </Content>
        </DrawerContent>
        <DrawerFooter>
          <CancelButton onClick={closeDrawer}>キャンセル</CancelButton>
          <StyledButton disabled={!canSubmit} onClick={handleSubmit} loading={linkSitesLoading}>
            保存する
          </StyledButton>
        </DrawerFooter>
      </DrawerContainer>
    </Drawer>
  );
};

const Content = styled('div')(`
  height: 100%;
  width: 100%;
`);

const DrawerDescription = styled('div')(`
  font-size: 16px;
  color: #707889;
  margin-bottom: 10px;
`);

const DrawerCaption = styled('div')(`
  font-size: 12px;
  color: #707889;
  margin-top: 20px;
`);

const TooltipText = styled(Typography)(`
  font-size: 10px;
  color: #fff;
`);

const DrawerContainer = styled('div')(`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  min-width: 864px;
  width: 60%;
`);

const DrawerContent = styled('div')(`
  padding: 40px;
`);

const DrawerHeader = styled('div')(`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`);

const DrawerTitle = styled(Typography)(`
  font-weight: 700;
  font-size: 24px;
  color: ${COLOR_TEXT_MAIN};
`);

const DrawerFooter = styled('div')(`
  padding: 16px 40px;
  text-align: end;
  background-color:#F5F6F7;
  border-top: solid 1px rgba(230, 231, 232, 1);
`);

const HelpIcon = styled(_HelpIcon)(`
  font-size: 12px;
`);

const StyledButton = styled(LoadingButton)(`
  color: #FFFFFF;
  height: 32px;
  width: 128px;
  font-size: 14px;
  background: ${COLOR_TEXT_MAIN};
  font-weight: 800;
  border-color: ${COLOR_TEXT_MAIN};
  border-radius: 4px;
  :hover {
    background: #707889;
    border-color: #707889;
  }
  :disabled {
    background: #E6E7E8;
    color: rgba(75, 85, 106, 0.24);
    border: rgba(75, 85, 106, 0.24);
  }
`);

const CancelButton = styled(Button)(`
  color: ${COLOR_TEXT_MAIN};
  width: 128px;
  height: 32px;
  font-size: 14px;
  background: #FFFFFF;
  font-weight: 800;
  border: 1.5px solid #E6E7E8;
  border-radius: 4px;
  margin-right: 16px;
  :hover {
    background: #FFFFFF;
  }
`);

const Radio = styled(_Radio)(`
  padding: 8px 12px;
`);

const GridItem = styled(Grid)(`
  padding: 8px 12px;
`);

export default AvailableSitesDrawer;
